import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button, Grid, Tooltip } from '@mui/material';
import { forEach, get, last, map, max, min, noop, pick, throttle } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { GuideDataType } from '../Gude.types';
import Steps from './Steps';
import {
	elementBeanListType,
	PublishedClickEvent,
	publishElementsDataType,
} from '../../../Flo.types';
import { scaleLinear } from 'd3-scale';
import { Player } from '@lottiefiles/react-lottie-player';
import mouseAnimation from '../../../../../Components/VideoPlayer/mouse-animation.json';
import { RootState } from '../../../../../store';
import { calculateAspectRatioFit } from '../../../../../utils/video.utils';
import BrandImage from '../../../Components/BrandImage/BrandImage';
import Annotations from '../../../../../Components/Annotations/Annotations';
import {
	currentProfileSelector,
	enablePoweredBYSelector,
} from '../../../../../Common/Common.selector';
import { AnnotationSnapshot } from '../../../../../Components/Annotations/Annotations.types';
import Konva from 'konva';
import Cookie from 'js-cookie';
import { ReactComponent as ProcessingSvg } from './ProcessingLoader.svg';
import { showAddCommentsPopup, trackClickAction } from '../../../Flo.reducers';
import { ReactComponent as FloikLogo } from '../../../../../Common/images/floik-logo-grey.svg';
import {
	getContrastColor,
	getWindowDimensions,
	isMicroApp,
	preventDefaultForTouchEndEvent,
	sanitizeHtml,
	sanitizeHtmlLinks,
} from '../../../../../Common/Common.utils';
import { APP_WEBSITE_URL } from '../../../../../Common/Common.env';
import { ImageElement } from '../../../../../Components/ImageElement/ImageElement';
import ShareFloButton from '../../../../../Components/ShareFloButton/ShareFloButton';
import SpotlightSvg from '../../../../../Common/Components/SpotlightSvg/SpotlightSvg';
import GuideImg from '../../../Components/GuideImg/GuideImg';
import KonvaEventObject = Konva.KonvaEventObject;
import parentStyles from '../../../Flo.module.css';
import styles from './Instruction.module.css';
import GuideVideo from '../../../Components/GuideVideo/GuideVideo';
import { GuideTextElement } from '../../../../../Components/TextElement/GuideTextElement';
import Wrapper from './Wrapper';
import { IconArrowLeft, IconArrowRight, IconRotate2 } from '@tabler/icons-react';
import SlideShow from './SlideShow';

const errorImage = new Image();
errorImage.src = 'https://zeus.floik.com/system/w3/media/images/not-found-avatar.svg';

const Processing = ({ text }: { text: string }) => (
	<div className={styles.processingLoaderContainer}>
		<ProcessingSvg className={styles.processingSvg} />
		<span className={styles.processingText}>{text}</span>
	</div>
);

const timestamp = '1697632494675';
const BASE_INTENSITY = 7;
const BASE_INTENSITY_MULTIPLIER = 13;

type BlurPointsType = {
	height: number;
	width: number;
	left: number;
	top: number;
};

type TransformedBlurItemType = Partial<
	elementBeanListType & {
		points: BlurPointsType;
	}
>;

const isDev = process.env.NODE_ENV === 'development';

const GuideBlurComp = ({
	data,
	isSpotlightElement,
}: {
	data: TransformedBlurItemType;
	isSpotlightElement?: boolean;
}) => {
	const finalIntensity = isSpotlightElement
		? 0
		: BASE_INTENSITY +
		  (get(data, 'properties.intensity', 0) * BASE_INTENSITY_MULTIPLIER) / 100;

	if (!get(data, 'points.height', 0) || !get(data, 'points.width', 0)) return <></>;

	return (
		<div className={styles.wrapper}>
			{isSpotlightElement ? (
				<svg className={styles.spotLightSvg}>
					<path className={styles.spotLightPath} d={get(data, 'points.path', '')}></path>
				</svg>
			) : (
				<div
					className={styles.elementWrapper}
					style={{
						height: get(data, 'points.height', 0),
						width: get(data, 'points.width', 0),
						top: get(data, 'points.top', 0),
						left: get(data, 'points.left', 0),
						// @ts-ignore
						'--data-intensity': finalIntensity,
					}}
				>
					{get(data, 'properties.shape') === 'circle' ? (
						<div
							style={pick(get(data, 'points'), ['height', 'width'])}
							className={classNames(styles.circle, {
								[styles.editing]: isDev,
							})}
						></div>
					) : (
						<div
							style={pick(get(data, 'points'), ['height', 'width'])}
							className={classNames(styles.rectangle, {
								[styles.editing]: isDev,
							})}
						></div>
					)}
				</div>
			)}
		</div>
	);
};
const GuideBlur = React.memo(GuideBlurComp);

const StepDetails = ({
	step,
	element,
	index,
	blursList,
	imagesList,
	spotlights,
	texts,
	setRenderStatus,
	floId,
	floIndex,
	themeColor,
	wrapper,
	isSlideShow,
	handleNext,
	handlePrevious,
	handleStepChange,
	totalSlides,
	showPoweredBy,
	onPoweredByClick,
	calculateHeight,
	onRefReady,
	containerSize,
	floElements,
	steps,
	aspectRatio,
}: {
	step: GuideDataType;
	element?: PublishedClickEvent;
	index: number;
	blursList?: elementBeanListType[];
	spotlights?: elementBeanListType[];
	texts?: elementBeanListType[];
	imagesList?: elementBeanListType[];
	setRenderStatus?: (index: number) => void;
	floId: string;
	floIndex: number;
	themeColor: string;
	wrapper?: string;
	isSlideShow: boolean;
	handleNext?: Function;
	handlePrevious?: Function;
	handleStepChange?: Function;
	totalSlides: number;
	showPoweredBy?: boolean;
	onPoweredByClick?: () => void;
	calculateHeight?: number;
	onRefReady: (element: HTMLElement | null) => void;
	containerSize?: { width: number; height: number };
	floElements: elementBeanListType[];
	steps: GuideDataType[];
	aspectRatio: number;
}) => {
	const dispatch = useDispatch();
	const enableAnnotation = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.allow_public_comments`, false)
	);
	const floImageElements = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floPrimaryTrack.floImageElements`)
	);
	const [videoReady, setVideoReady] = React.useState(false);
	const containerRef = useRef<HTMLLIElement>(null);
	const imageContainerRef = useRef<HTMLDivElement>(null);
	const [imageReadyState, setImageReady] = useState<'loading' | 'loaded' | 'errored'>(
		'loading'
	);
	const videoRef = useRef<HTMLVideoElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const videoPlayRetryTimeoutRef = useRef(null);
	const [animationTimeouts, setanimationTimeouts] = useState([]);
	const [videoStarted, setVideoStarted] = useState<boolean>(false);
	const [videoSize, setVideoSize] = useState<
		{ height: number; width: number } | null | undefined
	>({ width: 0, height: 0 });
	const [videoClickTime, setVideoClickTime] = useState(0);
	const isVideo = get(step, 'animateClick');
	const videoDuration = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.floResources.recordDuration`)
	);

	let token = '';
	const embeddedElements = document.querySelectorAll('floik-flo');
	if (embeddedElements[floIndex]) {
		const deploymentId = embeddedElements[floIndex].getAttribute('doc360-deployment-id');
		const basePathParam =
			embeddedElements[floIndex].getAttribute('doc360-base-path') || '';
		if (deploymentId && basePathParam) {
			token = useSelector((state) =>
				!get(state, 'routerContainer.externalAppInfo.ignoreCustomToken', '')
					? get(state, 'routerContainer.externalAppInfo.token', '')
					: ''
			);
		}
	}

	const [imageTransform, setImageTransform] = useState({});
	const [isFullScreen, setIsFullScreen] = useState(false);
	const [firstInteractionComplete, setFirstInteractionComplete] = useState(false);
	const [zoomed, setZoomed] = useState<boolean>(
		get(step, 'zoomInEffect') && !get(step, 'animateClick')
	);
	const [transitionEnd, setTransitionEnd] = useState(false);
	const [imageVisible, setImageVisible] = useState<boolean>(false);
	const [{ width, height }, setImageContainerDims] = useState({ width: 0, height: 0 });
	const [imgSrc, setImgSrc] = useState(
		get(step, 'resourceDetail.url') + `?timestamp=${timestamp}`
	);
	const [stepBlurs, setStepBlurs] = useState<TransformedBlurItemType[]>([]);
	const [stepSpotlights, setStepSpotlights] = useState<TransformedBlurItemType[]>([]);
	const [stepTexts, setStepTexts] = useState<TransformedBlurItemType[]>([]);
	const [images, setImages] = useState<Partial<publishElementsDataType>[]>([]);
	const [imageDims, setImageDims] = useState({
		width: 0,
		height: 0,
		top: 0,
		left: 0,
	});
	const imageRef = useRef<HTMLImageElement>(null);
	const enableAddComment = useSelector((state) => {
		const defaultValue = {
			visible: false,
			annotationsMeta: [],
			id: '',
			top: 0,
			left: 0,
			question: '',
			name: '',
			email: '',
			className: '',
		};
		const popupData = get(state, `floPage.flos.${floIndex}.commentsPopup`, defaultValue);
		if (get(popupData, 'id') === step.id) {
			return popupData;
		}
		return defaultValue;
	});
	const imageActualWidth = get(
		element,
		'properties.canvasRenderConfig.windowCoordinates.width',
		0
	);
	const xScale = scaleLinear(
		[0, get(element, 'properties.canvasRenderConfig.windowCoordinates.width', 0)],
		[0, isSlideShow ? imageDims.width : width]
	);
	const currentUser = useSelector(currentProfileSelector);
	const imageActualHeight = get(
		element,
		'properties.canvasRenderConfig.windowCoordinates.height',
		0
	);
	const yScale = scaleLinear(
		[0, get(element, 'properties.canvasRenderConfig.windowCoordinates.height', 0)],
		[0, isSlideShow ? imageDims.height : height]
	);
	const padding = 10;
	const enableZoom = get(step, 'zoomInEffect');
	const elementAvailable = // @ts-ignore
		isFinite(get(element, 'properties.canvasRenderConfig.closestElementBounds.x')) &&
		(imageReadyState === 'loaded' || videoReady);
	const elemX = isSlideShow
		? xScale(
				get(element, 'properties.canvasRenderConfig.closestElementBounds.x', 0) -
					padding / 2
		  )
		: xScale(
				get(element, 'properties.canvasRenderConfig.closestElementBounds.x', 0) -
					padding / 2
		  );
	const elemY = isSlideShow
		? yScale(
				get(element, 'properties.canvasRenderConfig.closestElementBounds.y', 0) -
					padding / 2
		  )
		: yScale(
				get(element, 'properties.canvasRenderConfig.closestElementBounds.y', 0) -
					padding / 2
		  );

	const elemWidth = isSlideShow
		? xScale(
				get(element, 'properties.canvasRenderConfig.closestElementBounds.width', 0) +
					padding
		  )
		: xScale(
				get(element, 'properties.canvasRenderConfig.closestElementBounds.width', 0) +
					padding
		  );
	const elemHeight = isSlideShow
		? yScale(
				get(element, 'properties.canvasRenderConfig.closestElementBounds.height', 0) +
					padding
		  )
		: yScale(
				get(element, 'properties.canvasRenderConfig.closestElementBounds.height', 0) +
					padding
		  );
	const canvasX = get(element, 'properties.canvasRenderConfig.canvasCoordinates.x', 0);
	const canvasY = get(element, 'properties.canvasRenderConfig.canvasCoordinates.y', 0);
	const pointerX = isSlideShow
		? xScale(canvasX)
		: imageActualWidth < (imageContainerRef?.current?.offsetWidth || 0)
		? canvasX
		: xScale(canvasX);
	const pointerY = isSlideShow
		? yScale(canvasY)
		: imageActualHeight < (imageContainerRef?.current?.offsetHeight || 0)
		? canvasY
		: yScale(canvasY);

	const getTransformedBlur = useCallback(
		(values: elementBeanListType[], isSpotlight?: boolean) => {
			return values.map((blurItem) => {
				const targetWidth = isSlideShow ? imageDims.width : width;
				const targetHeight = isSlideShow ? imageDims.height : height;

				const xScale = scaleLinear(
					[0, get(blurItem, 'properties.canvasRenderConfig.windowCoordinates.width', 0)],
					[0, targetWidth]
				);
				const yScale = scaleLinear(
					[0, get(blurItem, 'properties.canvasRenderConfig.windowCoordinates.height', 0)],
					[0, targetHeight]
				);

				const canvasX = get(
					blurItem,
					'properties.canvasRenderConfig.canvasCoordinates.x',
					0
				);
				const canvasY = get(
					blurItem,
					'properties.canvasRenderConfig.canvasCoordinates.y',
					0
				);
				const elemWidth = get(
					blurItem,
					'properties.canvasRenderConfig.canvasCoordinates.width',
					0
				);
				const elemHeight = get(
					blurItem,
					'properties.canvasRenderConfig.canvasCoordinates.height',
					0
				);
				const angle = get(
					blurItem,
					'properties.canvasRenderConfig.canvasCoordinates.angle',
					0
				);

				return {
					...blurItem,
					shape: get(blurItem, 'properties.shape'),
					points: {
						left: xScale(canvasX),
						width: xScale(elemWidth),
						height: yScale(elemHeight),
						top: yScale(canvasY),
						angle,
					},
				};
			});
		},
		[imageDims.width, imageDims.height, width, height, isSlideShow]
	);

	const isMicroView = isMicroApp();

	useEffect(() => {
		if (isMicroView && containerRef.current?.querySelector('h4')) {
			setRenderStatus?.(index);
		}
	}, [containerRef.current, setRenderStatus, isMicroView]);

	useEffect(() => {
		setStepBlurs(getTransformedBlur(blursList || []));
	}, [blursList, getTransformedBlur, setStepBlurs]);

	useEffect(() => {
		setStepSpotlights(getTransformedBlur(spotlights || [], true));
	}, [spotlights, getTransformedBlur, setStepSpotlights]);

	// useEffect(() => {
	// 	setStepTexts(getTransformedBlur(texts || [], true));
	// }, [texts, getTransformedBlur, setStepTexts]);

	useEffect(() => {
		if (
			!imageContainerRef.current ||
			(isVideo ? videoReady : imageReadyState !== 'loaded')
		)
			return;
		const observerCallback = (
			entries: IntersectionObserverEntry[],
			observer: IntersectionObserver
		) => {
			setImageVisible(get(entries, '0.isIntersecting', false));
			if (get(entries, '0.isIntersecting', false)) {
				observer.disconnect();
			}
		};
		const interSectionObserver = new IntersectionObserver(observerCallback, {
			root: null,
			rootMargin: '0px',
			threshold: 0.5,
		});
		interSectionObserver.observe(imageContainerRef.current);
		return () => {
			interSectionObserver.disconnect();
		};
	}, [imageContainerRef, imageReadyState, setImageVisible, isVideo, videoReady]);

	useEffect(() => {
		if (isVideo && imageContainerRef.current) {
			const imageContainerDims = imageContainerRef.current.getBoundingClientRect();
			const videoDimensions = {
				width: videoRef.current?.videoWidth || 0,
				height: videoRef.current?.videoHeight || 0,
			};

			if (videoDimensions.width === 0 || videoDimensions.height === 0) {
				// If video dimensions are not available yet, wait for them to be loaded
				return;
			}

			const imageContainerHeight =
				(videoDimensions.height * (imageContainerDims.width - 2)) / videoDimensions.width;

			// Calculate video size
			const newVideoSize = calculateAspectRatioFit(
				videoDimensions.width,
				videoDimensions.height,
				imageContainerDims.width,
				imageContainerHeight
			);

			setVideoSize(newVideoSize);

			// Update image dimensions for proper mouse positioning
			if (videoReady && videoRef.current) {
				if (isSlideShow) {
					setImageDims({
						width: videoRef.current?.getBoundingClientRect().width || 0,
						height: videoRef.current?.getBoundingClientRect().height || 0,
						top: videoRef.current?.getBoundingClientRect().top || 0,
						left: videoRef.current?.getBoundingClientRect().left || 0,
					});
				} else {
					const videoTop = (imageContainerDims.height - newVideoSize.height) / 2;
					const videoLeft = (imageContainerDims.width - newVideoSize.width) / 2;

					setImageDims({
						width: newVideoSize.width,
						height: newVideoSize.height,
						top: videoTop,
						left: videoLeft,
					});
				}
			}
		}
	}, [
		imageContainerRef.current,
		isVideo,
		videoRef.current,
		videoRef.current?.videoWidth,
		videoRef.current?.videoHeight,
		videoReady,
		isSlideShow,
	]);

	useEffect(() => {
		if (imageContainerRef?.current && (imageReadyState === 'loaded' || videoReady)) {
			const onresize = () => {
				if (!imageContainerRef?.current) return;
				const imageContainer = imageContainerRef.current.getBoundingClientRect();
				const img = imageRef.current;
				const imageContainerDims = {
					width: imageContainer.width - 2, // 2 is border widths
					height: imageContainer.height - 2,
				};

				const mediaElementDimensions = isVideo
					? videoSize
					: isSlideShow
					? {
							width: imageDims.width,
							height: imageDims.height,
					  }
					: {
							width: get(img, 'naturalWidth', 0),
							height: get(img, 'naturalHeight', 0),
					  };
				setImageContainerDims(imageContainerDims);
				const cropRatio = 0.6;

				const imgDims = calculateAspectRatioFit(
					mediaElementDimensions?.width || 0,
					mediaElementDimensions?.height || 0,
					imageContainerDims.width,
					imageContainerDims.height
				);

				const zoomedImageDims = {
					width: imgDims.width / cropRatio,
					height: imgDims.height / cropRatio,
				};

				const cropWidth = imageContainerDims.width;
				const cropHeight = imageContainerDims.height;

				const pointerX = scaleLinear(
					[
						0,
						get(element, 'properties.canvasRenderConfig.windowCoordinates.width') ||
							zoomedImageDims.width,
					],
					[0, zoomedImageDims.width]
				)(get(element, 'properties.canvasRenderConfig.canvasCoordinates.x') || 0);

				const pointerY = scaleLinear(
					[
						0,
						get(element, 'properties.canvasRenderConfig.windowCoordinates.height') ||
							zoomedImageDims.height,
					],
					[0, zoomedImageDims.height]
				)(get(element, 'properties.canvasRenderConfig.canvasCoordinates.y') || 0);

				let left = pointerX - cropWidth / 2;
				let right = pointerX + cropWidth / 2;

				let top = pointerY - cropHeight / 2;
				let bottom = pointerY + cropHeight / 2;

				let diffX = 0;
				if (left < 0) {
					diffX = left; // Clamp to 0 (minimum left position)
				}

				const drx = zoomedImageDims.width - right;
				left = left - diffX + (drx > 0 ? 0 : drx);
				right = right - diffX + (drx > 0 ? 0 : drx);

				let diffY = 0;
				const dry = zoomedImageDims.height - bottom;

				if (top < 0) {
					diffY = top; // Clamp to 0 (minimum top position)
				}
				bottom = bottom - diffY + (dry > 0 ? 0 : dry);
				top = top - diffY + (dry > 0 ? 0 : dry);

				// Adjust for imgDims.top and imgDims.left when in slideshow mode
				if (isSlideShow) {
					// Consider imgDims.top and imgDims.left in calculations
					const slideShowLeft = isSlideShow ? left + (imageDims.left || 0) : left;
					const slideShowTop = isSlideShow ? top + (imageDims.top || 0) : top;

					setImageTransform({
						transform: `scale(${1 / cropRatio}) 
							 translate(${slideShowLeft * -1 * 0.6}px, ${slideShowTop * -1 * 0.6}px)
							`,
						transformOrigin: '0 0',
					});
				} else {
					setImageTransform({
						transform: `scale(${1 / cropRatio}) translate(${left * -1 * 0.6}px, ${
							top * -1 * 0.6
						}px)`,
						transformOrigin: '0 0',
					});
				}
			};
			const resizeObserver = new ResizeObserver(
				throttle(onresize, 300, {
					leading: false,
					trailing: true,
				})
			);
			resizeObserver.observe(imageContainerRef?.current);
			onresize();
		}
	}, [
		imageDims,
		imageContainerRef?.current,
		imageReadyState,
		setImageContainerDims,
		setImageTransform,
		videoSize?.width,
		videoSize?.height,
		isVideo,
		videoReady,
	]);

	const onTransitionEnd = useCallback(() => {
		if (transitionEnd && !firstInteractionComplete) {
			setZoomed(true);
		}
		setFirstInteractionComplete(true);
		setTransitionEnd(true);
	}, [firstInteractionComplete, transitionEnd, setZoomed, setTransitionEnd]);

	const onZoomOut = useCallback(() => {
		setZoomed(false);
	}, [setZoomed]);

	const onZoomIn = useCallback(() => {
		setZoomed(true);
	}, [setZoomed]);

	const onLoadedImage = useCallback(() => {
		if (
			imageRef?.current?.src ===
			get(step, 'resourceDetail.url') + `?timestamp=${timestamp}`
		) {
			setImageReady('loaded');

			// Fix image size to fit container properly if in slide show mode
			if (isSlideShow && imageRef.current) {
				// Check if title and description are missing to determine styling
				Object.assign(imageRef.current.style, {
					maxWidth: '100%',
					maxHeight: '100%',
					width: 'auto',
					height: 'auto',
					objectFit: 'contain',
				});
			}
		}
		// @ts-ignore
	}, [imageRef, setImageReady, get(step, 'resourceDetail.url'), isSlideShow, step]);

	const onLoadImageError = useCallback(() => {
		setImageReady('errored');
		// @ts-ignore
		setImgSrc('https://zeus.floik.com/system/w3/media/images/not-found-avatar.svg');
	}, [imageRef, setImageReady, setImgSrc]);

	useEffect(() => {
		setImages(
			map(imagesList, (image) => {
				const imageId = get(image, 'properties.imageId', '');
				const imageUrl =
					get(floImageElements, image?.id || '') || get(floImageElements, imageId);
				return {
					...image,
					imageUrl,
				};
			})
		);
	}, [imagesList, setImages, floImageElements, step]);

	const addAnnotationHandler = useCallback(
		(data: AnnotationSnapshot, event?: KonvaEventObject<MouseEvent>) => {
			const value = last(data.state) || { x: 0, y: 0, height: 0, width: 0 };
			let offset = 0;
			if (containerRef?.current) {
				const { x } = containerRef.current.getBoundingClientRect();
				offset = x;
			}
			const windowDimensions = getWindowDimensions(floIndex);
			dispatch(
				showAddCommentsPopup({
					data: {
						visible: true,
						annotationsMeta: [value],
						id: step.id,
						time: step.order,
						top: min([
							get(value, 'y', 0),
							max([windowDimensions.height - 280, 0]),
						]) as number,
						left: min([
							offset + get(value, 'x', 0) + get(value, 'width', 0) + 10,
							max([windowDimensions.width - 300, 0]),
						]) as number,
						question: '',
						name: Cookie.get('floik-username') || '',
						email: Cookie.get('floik-user-email') || '',
						className: '',
					},
					floId,
					floIndex,
				})
			);
		},
		[step, imageVisible, zoomed, floId]
	);

	const onResizeImage = useCallback(() => {
		setImageDims({
			width: imageRef.current?.offsetWidth || 0,
			height: imageRef.current?.offsetHeight || 0,
			top: isSlideShow ? imageRef.current?.offsetTop || 0 : 0,
			left: isSlideShow ? imageRef.current?.offsetLeft || 0 : 0,
		});
	}, [setImageDims, imageRef]);

	useEffect(() => {
		if (!imageRef.current) return;
		const observer = new ResizeObserver(onResizeImage);
		observer.observe(imageRef.current);

		return () => {
			observer.disconnect();
		};
	}, [onResizeImage, imageRef.current]);

	const xInRange = pointerX >= elemX && pointerX <= elemX + elemWidth;
	const yInRange = pointerY >= elemY && pointerY <= elemY + elemHeight;
	const pointerInRange = xInRange && yInRange;
	const showBorder = elementAvailable && pointerInRange && !isMicroApp();

	const scrollIntoView = (event: any) => {
		const button = event.target;

		// Scroll the focused button into view if it's not in view
		button.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest', // Ensure it scrolls into the nearest visible area of the container
		});
	};

	const handleFocus = () => {
		// @ts-ignore
		imageContainerRef.current.setAttribute('data-focus', 'true');
	};

	// Handle blur event
	const handleBlur = () => {
		// @ts-ignore
		imageContainerRef.current.removeAttribute('data-focus');
	};

	// Remove focus class on click
	const handleClick = () => {
		// @ts-ignore
		imageContainerRef.current.removeAttribute('data-focus');
	};

	const onVideoLoad = useCallback(() => {
		setVideoReady(true);
	}, []);

	const playVideo = useCallback(() => {
		videoRef.current
			?.play()
			.then(() => {
				if (videoPlayRetryTimeoutRef.current) {
					clearTimeout(videoPlayRetryTimeoutRef.current);
					videoPlayRetryTimeoutRef.current = null;
				}
			})
			.catch(() => {
				videoPlayRetryTimeoutRef.current = setTimeout(() => {
					playVideo();
				}, 200);
			});
		return () => {
			if (videoPlayRetryTimeoutRef.current) {
				clearTimeout(videoPlayRetryTimeoutRef.current);
			}
		};
	}, [videoRef.current, videoPlayRetryTimeoutRef.current]);

	useEffect(() => {
		if (videoRef.current && !videoStarted) {
			setVideoStarted(true);
			playVideo();
		}
	}, [videoRef.current, videoStarted, playVideo]);

	const animateClick = useCallback(() => {
		if (wrapperRef.current && wrapperRef.current.style) {
			wrapperRef.current.style.transition = 'transform 0.35s ease-out 0s';
			setZoomed(true);
			let timeouts = [];
			timeouts.push(
				setTimeout(() => {
					setZoomed(false);
				}, 1000)
			);
			timeouts.push(
				setTimeout(() => {
					if (wrapperRef.current) {
						wrapperRef.current.style.transition = 'transform 0.25s ease-out 0s';
					}
					videoRef.current?.play().catch(() => {});
					setanimationTimeouts([]);
				}, 1350)
			);
			setanimationTimeouts(timeouts);
		}
		return () => {
			if (animationTimeouts.length) {
				forEach(animationTimeouts, (timeout) => {
					clearTimeout(timeout);
				});
			}
		};
	}, [wrapperRef.current, videoRef.current, animationTimeouts]);

	useEffect(() => {
		const animationDuration = get(step, 'animationDuration') || 0;
		const originalClickTime =
			get(step, 'manuallySetFrameTime') || get(element, 'properties.time') || 0;
		const offset = animationDuration / 2;
		let clickTime = offset;

		if (videoDuration && originalClickTime + offset > videoDuration) {
			const startTime = videoDuration - animationDuration;
			clickTime = originalClickTime - startTime;
		}
		if (originalClickTime - offset < 0) {
			clickTime = originalClickTime;
		}
		setVideoClickTime(clickTime);
	}, [step, element, videoDuration]);

	const onTimeUpdate = useCallback(() => {
		const currentTime = videoRef.current?.currentTime;

		if (currentTime >= videoClickTime - 0.1 && currentTime <= videoClickTime + 0.1) {
			videoRef.current.pause();
			animateClick();
		}
	}, [videoRef.current, animateClick, videoClickTime]);

	useEffect(() => {
		onRefReady && onRefReady(containerRef.current);
	}, [onRefReady]);

	const [optionsVisible, setOptionsVisible] = useState(false);
	const optionsRef = useRef<HTMLDivElement | null>(null);

	const handleSpanClick = () => {
		setOptionsVisible(true);
	};

	const handleOptionClick = (value: any) => {
		if (handleStepChange) {
			const event = { target: { value: value } };
			handleStepChange(event);
		}
		setOptionsVisible(false);
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (optionsRef.current && !optionsRef.current.contains(event.target as Node)) {
			setOptionsVisible(false);
		}
	};

	useEffect(() => {
		if (optionsVisible) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [optionsVisible]);
	const spanRef = useRef(null);
	const getOptionsTopPosition = () => {
		if (spanRef.current) {
			const spanRect = spanRef.current.getBoundingClientRect();
			return spanRect.top - Math.min(5, totalSlides) * 30; // 30px is an approximate height of an option
		}
		return 0;
	};

	// Add these declarations at the top of the StepDetails component, after other variable declarations
	const hasDescription = Boolean(step.stepDescription && step.stepDescription.trim());
	const hasImage = Boolean(get(step, 'resourceDetail.url'));

	// Handle rendering when title, description, or image are missing
	const hasTitleOrDescription = Boolean(step.title || step.stepDescription);

	// Use passed container size if available, otherwise calculate it locally
	const contentRef = useRef<HTMLDivElement>(null);
	const [localContainerSize, setLocalContainerSize] = useState({
		width: containerSize?.width || 0,
		height: containerSize?.height || 0,
	});

	// Use container size passed from parent if available
	const effectiveContainerSize =
		containerSize?.width && containerSize?.height ? containerSize : localContainerSize;

	// Calculate local container size if not provided by parent
	useEffect(() => {
		if (isSlideShow && !containerSize && contentRef.current) {
			// Get window dimensions and calculate container size
			const windowHeight = window.innerHeight;
			const windowWidth = window.innerWidth;

			// Set fixed container size - leave 80px for controls and padding
			const fixedHeight = windowHeight - 180;
			// Use 4:3 aspect ratio as default or calculate based on available width
			const fixedWidth = Math.min(windowWidth - 40, (fixedHeight * 4) / 3);

			setLocalContainerSize({ width: fixedWidth, height: fixedHeight });
		}
	}, [isSlideShow, containerSize, contentRef.current, setLocalContainerSize]);

	// Add this function to calculate aspect ratio
	const calculateAspectRatio = (width: number, height: number) => {
		// Round to 2 decimal places to group similar ratios
		return Math.round((width / height) * 100) / 100;
	};

	// Update the findMostCommonAspectRatio function to use the prop
	const findMostCommonAspectRatio = useCallback(
		(steps: GuideDataType[]) => {
			const aspectRatios = new Map<number, number>();

			steps.forEach((step, index) => {
				const element = get(floElements, step.elementId);
				if (!element) {
					return;
				}

				const width = get(
					element,
					'properties.canvasRenderConfig.windowCoordinates.width',
					0
				);
				const height = get(
					element,
					'properties.canvasRenderConfig.windowCoordinates.height',
					0
				);

				if (width && height) {
					const ratio = calculateAspectRatio(width, height);
					aspectRatios.set(ratio, (aspectRatios.get(ratio) || 0) + 1);
				}
			});

			// Find the most common aspect ratio
			let maxCount = 0;
			let mostCommonRatio = 16 / 9; // Default fallback

			aspectRatios.forEach((count, ratio) => {
				if (count > maxCount) {
					maxCount = count;
					mostCommonRatio = ratio;
				}
			});

			return mostCommonRatio;
		},
		[floElements]
	);

	// Update the calculateImageDimensions function
	const calculateImageDimensions = useCallback(() => {
		if (!isSlideShow || !imageContainerRef.current) {
			return;
		}

		// Get the most common aspect ratio from all steps
		//const mostCommonRatio = findMostCommonAspectRatio(steps);

		// Check if title and description are missing
		const hasTitleContent = Boolean(step.title && step.title.trim());
		const shouldExpandImage = !hasTitleContent && !hasDescription;

		// Get available space for image after accounting for title, description and controls
		const titleHeight = hasTitleContent ? 42 : 0; // Height for title area if present
		const descriptionHeight = hasDescription ? 72 : 0; // Description + margin if present
		const padding = 16; // Additional padding
		const toolbarHeight = 32; // Height of the control bar
		const margins = 32; // Total margins (16px top + 16px bottom)

		// Calculate available height
		const nonImageHeight =
			titleHeight + descriptionHeight + padding + toolbarHeight + margins;
		const availableHeight = Math.max(effectiveContainerSize.height - nonImageHeight, 150);

		// Update image container style directly
		if (imageContainerRef.current) {
			// When no title and description, let image take full height minus margins and toolbar
			const containerHeight = shouldExpandImage
				? effectiveContainerSize.height - margins - toolbarHeight
				: availableHeight;

			// Set container dimensions based on aspect ratio
			const containerWidth = containerHeight * aspectRatio;

			// Ensure container has proper minimum dimensions
			imageContainerRef.current.style.height = `${Math.max(containerHeight, 240)}px`;
			imageContainerRef.current.style.width = `${Math.max(containerWidth, 320)}px`;
			imageContainerRef.current.style.maxWidth = `${effectiveContainerSize.width}px`;
			imageContainerRef.current.style.overflow = 'hidden';

			// Center the container
			imageContainerRef.current.style.margin = '0 auto';
			imageContainerRef.current.style.display = 'flex';
			imageContainerRef.current.style.justifyContent = 'center';
			imageContainerRef.current.style.alignItems = 'center';

			// Set the image/video dimensions to match the container
			// if (imageRef.current) {
			// 	// For images, ensure they fit within the container while maintaining aspect ratio
			// 	Object.assign(imageRef.current.style, {
			// 		maxWidth: '100%',
			// 		maxHeight: '100%',
			// 		width: 'auto',
			// 		height: 'auto',
			// 		objectFit: 'contain',
			// 	});
			// }
		}
	}, [
		isSlideShow,
		isVideo,
		videoRef,
		videoReady,
		imageDims,
		effectiveContainerSize,
		step,
		steps,
		floElements,
		findMostCommonAspectRatio,
		hasDescription,
	]);

	// Update the useEffect for image dimensions
	useEffect(() => {
		if (isSlideShow) {
			calculateImageDimensions();
		}
	}, [
		isSlideShow,
		effectiveContainerSize,
		step,
		calculateImageDimensions,
		videoRef.current?.videoWidth,
		videoRef.current?.videoHeight,
		videoReady,
	]);

	// Determine classes based on what content is available
	const getContentClasses = () => {
		if (isSlideShow) {
			if (!hasTitleOrDescription) {
				// No image but has title/description - center content
				return styles.slideDescriptionDivNoImage;
			}
		}
		return '';
	};

	return (
		<li
			tabIndex={0}
			onFocus={!isMicroApp() ? scrollIntoView : noop}
			ref={containerRef}
			id={`step-${step.id}`}
			className={classNames(
				'options-box',
				isSlideShow ? styles.slideShowLI : '',
				isSlideShow && !hasTitleOrDescription ? styles.noImage : ''
			)}
		>
			<div
				ref={contentRef}
				className={classNames(styles.content, {
					[styles.slideShowContent]: isSlideShow,
				})}
				style={{
					background: themeColor?.includes('gradient') ? themeColor : undefined,
					backgroundColor:
						themeColor && !themeColor.includes('gradient') ? themeColor : '#fff',
					...(isSlideShow && effectiveContainerSize.width > 0
						? {
								width: effectiveContainerSize.width,
								height: effectiveContainerSize.height,
						  }
						: {}),
					...(isSlideShow && effectiveContainerSize.height > 0
						? {
								maxHeight: 'calc(100vh - 80px)',
						  }
						: {}),
				}}
			>
				{/* Content area - will flex to fill available space */}
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						width: '100%',
						height: '100%',
						overflow: !hasImage ? 'auto' : 'hidden',
						gap: '8px',
					}}
				>
					{/* Title container */}
					{(!isSlideShow || (isSlideShow && step.title && step.title.trim())) && (
						<div
							className="instruction-head flex align-baseline hr-space-between"
							style={{
								...(isSlideShow &&
									!hasImage && {
										padding: '16px 0',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										textAlign: 'center',
										flexShrink: 0,
									}),
								...(isSlideShow &&
									hasImage && {
										padding: '0',
										display: 'flex',
										justifyContent: 'flex-start',
										alignItems: 'flex-start',
										textAlign: 'left',
										width: '100%',
										flexShrink: 0,
										marginTop: '0',
										marginBottom: '0',
										overflow: 'hidden',
										...(hasDescription ? { maxHeight: '45px' } : { maxHeight: '100px' }), // If only title and image, maxHeight is 100px
									}),
							}}
						>
							{!isSlideShow && (
								<div
									className={classNames(styles.index, styles.stepIndex, {
										[styles.stepIndexWithBrandBg]: themeColor === 'none',
									})}
									style={
										{
											'--index-color':
												themeColor === 'none'
													? 'var(--brand-color-instructions-list-step-index-color)'
													: getContrastColor(themeColor),
											'--index-background':
												themeColor === 'none'
													? 'var(--global-themeColor)'
													: getContrastColor(themeColor) === '#fff'
													? '#EFEFFB4D'
													: '#EFEFFB80',
											'--index-bg-opacity': themeColor === 'none' ? '0.08' : '1',
										} as React.CSSProperties
									}
								>
									<span className={styles.stepIndexText}>{index + 1}</span>
								</div>
							)}
							<div
								className={classNames(styles.stepMeta, {
									[styles.resetPadding]: isSlideShow,
									[styles.slideShowAlignText]: isSlideShow,
								})}
							>
								<div className={styles.instruction}>
									<div
										className={classNames('instruction-editor', styles.instructionTitle)}
										style={{
											textAlign: 'left',
											justifyContent: isSlideShow && !hasImage ? 'center' : 'flex-start',
											alignItems: 'flex-start',
											width: '100%',
										}}
									>
										{isMicroApp() ? (
											<h4
												className={classNames(styles.title, styles.nameEditor)}
												contentEditable={false}
												dangerouslySetInnerHTML={{ __html: sanitizeHtml(step.title) }}
												data-block-id={step.id}
											/>
										) : (
											<h2
												className={classNames(styles.title, styles.nameEditor)}
												style={{
													color: getContrastColor(themeColor),
												}}
												contentEditable={false}
												dangerouslySetInnerHTML={{ __html: sanitizeHtml(step.title) }}
												data-block-id={step.id}
											/>
										)}
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Description container */}
					{step.stepDescription && step.stepDescription.trim() && (
						<div
							className={classNames(
								styles.instruction,
								isSlideShow ? styles.slideDescriptionDiv : '',
								isSlideShow && !hasTitleOrDescription
									? styles.slideDescriptionDivNoImage
									: '',
								getContentClasses()
							)}
							style={{
								...(isSlideShow &&
									!hasImage && {
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'flex-start',
										padding: '0',
										margin: '0',
										textAlign: 'center',
										maxHeight: 'calc(100% - 120px)',
										overflow: 'auto',
										flexShrink: 1,
										scrollPaddingTop: '16px',
									}),
								...(isSlideShow &&
									hasImage && {
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'flex-start',
										alignItems: 'flex-start',
										padding: '0',
										margin: '0 0 0 0',
										textAlign: 'left',
										width: '100%',
										overflow: 'auto',
										...(step.title && step.title.trim()
											? { maxHeight: '65px' }
											: { maxHeight: '120px' }), // If only description and image, maxHeight is 100px
									}),
							}}
						>
							<div
								className={classNames(
									'instruction-description',
									styles.instructionDescription
								)}
								style={{
									...(isSlideShow &&
										!hasImage && {
											maxWidth: '100%',
											margin: '0',
											padding: '0',
										}),
									...(isSlideShow &&
										hasImage && {
											maxWidth: '100%',
											margin: '0',
											padding: '0',
											textAlign: 'left',
										}),
								}}
							>
								<div
									className={classNames(styles.description, styles.nameEditor)}
									style={{
										...(isMicroApp() ? {} : { color: getContrastColor(themeColor) }),
										opacity: 0.7,
										...(isSlideShow &&
											!hasImage && {
												fontSize: '1.1rem',
												lineHeight: '1.5',
												textAlign: 'center',
											}),
										...(isSlideShow &&
											hasImage && {
												fontSize: '1rem',
												lineHeight: '1.5',
												textAlign: 'left',
											}),
									}}
									contentEditable={false}
									dangerouslySetInnerHTML={{
										__html: sanitizeHtmlLinks(step.stepDescription),
									}}
								/>
							</div>
						</div>
					)}
					{get(step, 'resourceDetail.url') && (
						<div
							className={classNames(
								styles.imageContainer,
								{
									[styles.slideImageDiv]: isSlideShow,
								},
								isSlideShow ? styles.slideshowBorder : '',
								isSlideShow && !hasTitleOrDescription
									? styles.imageContainerNoTitleDesc
									: ''
							)}
						>
							<Wrapper
								theme={wrapper}
								className={{
									[styles.hideHeaderBorder]: isSlideShow,
								}}
							/>
							<div
								ref={imageContainerRef}
								className={classNames(styles.screen, styles.stepImage, {
									[styles.justifyCenter]: !elementAvailable,
									[styles.imgWrapperScreen]: wrapper && wrapper != 'rounded',
								})}
								tabIndex={-1}
								onClick={handleClick}
							>
								{enableZoom && imageReadyState === 'loaded' && transitionEnd && (
									<div className={styles.magnification}>
										<Button
											disableElevation
											tabIndex={0}
											aria-label="Zoom out"
											variant="contained"
											className={classNames(styles.magnificationMinus, {
												[styles.active]: !zoomed,
											})}
											onClick={onZoomOut}
										>
											{' '}
											-{' '}
										</Button>
										<Button
											disableElevation
											tabIndex={0}
											aria-label="Zoom in"
											variant="contained"
											className={classNames(styles.magnificationPlus, {
												[styles.active]: zoomed,
											})}
											onClick={onZoomIn}
										>
											{' '}
											+{' '}
										</Button>
									</div>
								)}
								{imageReadyState === 'loaded' && (
									<BrandImage floId={floId} floIndex={floIndex} />
								)}
								<div
									id={`step_${step.id}`}
									style={{
										...(enableZoom && imageVisible && zoomed
											? imageTransform
											: isSlideShow
											? {}
											: {
													left: '50%',
													top: '50%',
													transform: 'translateX(-50%)',
													transformOrigin: '0 0',
											  }),
										...(isSlideShow
											? {
													position: 'relative',
													width: '100%',
													height: '100%',
													display: 'flex',
													justifyContent: 'center',
													alignItems: 'center',
											  }
											: {}),
									}}
									ref={wrapperRef}
									onTransitionEnd={onTransitionEnd}
									className={classNames(styles.stepImage, {
										[styles.loadingStepImage]: imageReadyState === 'loading',
										[styles.animate]:
											imageVisible && !transitionEnd && !firstInteractionComplete,
									})}
								>
									{imageReadyState === 'loading' && !isVideo && (
										<Processing text={'Loading...'} />
									)}
									{isVideo ? (
										<GuideVideo
											step={step}
											onLoad={onVideoLoad}
											onTimeUpdate={onTimeUpdate}
											videoRef={videoRef}
											videoSize={localContainerSize}
										/>
									) : (
										<GuideImg
											imageRef={imageRef}
											imgSrc={imgSrc}
											step={step}
											imageReadyState={imageReadyState}
											setImageReadyState={setImageReady}
											token={token}
											onLoadedImage={onLoadedImage}
											onLoadImageError={onLoadImageError}
											onFocus={handleFocus}
											onBlur={handleBlur}
											floIndex={floIndex}
										/>
									)}
									<div
										className={styles.imageOverlay}
										style={{
											top: isVideo
												? videoRef.current?.getBoundingClientRect().top
													? videoRef.current.offsetTop
													: 0
												: imageDims.top,
											left: isVideo
												? videoRef.current?.getBoundingClientRect().left
													? videoRef.current.offsetLeft
													: 0
												: imageDims.left,
											width: isVideo
												? videoRef.current?.getBoundingClientRect().width
													? videoRef.current.offsetWidth
													: 0
												: imageDims.width,
											height: isVideo
												? videoRef.current?.getBoundingClientRect().height
													? videoRef.current.offsetHeight
													: 0
												: imageDims.height,
										}}
									>
										{showBorder && (!get(step, 'animateClick') || zoomed) && (
											<div
												className={styles.annotation}
												style={{
													left: elemX,
													top: elemY,
													height: elemHeight,
													width: elemWidth,
												}}
											></div>
										)}

										{imageReadyState === 'loaded' && (
											<Annotations
												isImage
												// @ts-ignore
												currentUser={currentUser}
												videoSize={{
													width: imageActualWidth,
													height: imageActualHeight,
												}}
												onDone={addAnnotationHandler}
												addingComment={enableAddComment.visible}
												actionType={'add-comment'}
												isFloClosed={false}
												enable={enableAnnotation}
												elements={enableAddComment?.annotationsMeta}
												width={width}
												height={height}
												offsetWidth={imageDims.top}
												offsetHeight={imageDims.left}
												floId={floId}
												floIndex={floIndex}
											/>
										)}
										{stepBlurs?.length > 0 && (
											<div className={styles.blurListWrapper}>
												{/*@ts-ignore*/}
												{stepBlurs.map((blurItem) => (
													<GuideBlur data={blurItem} />
												))}
											</div>
										)}
										{texts && texts?.length > 0 && (
											<div className={styles.textListWrapper}>
												{/*@ts-ignore*/}
												{texts.map((item) => (
													<GuideTextElement
														key={`${get(item, 'id')}`}
														//@ts-ignore
														currentElement={item}
														idDocument={true}
														playerDim={isVideo ? videoSize : imageDims}
														width={isVideo ? videoSize?.width : imageDims.width}
														height={isVideo ? videoSize?.height : imageDims.height}
														floId={floId}
														floIndex={floIndex}
													/>
												))}
											</div>
										)}
										{(images || []).length > 0 && (
											<div className={styles.imagesWrapper}>
												{images?.map((imageItem) => (
													<ImageElement
														key={`${get(imageItem, 'id')}`}
														//@ts-ignore
														currentElement={imageItem}
														isDocument
														playerDim={isVideo ? videoSize : imageDims}
														parentLoading={imageReadyState === 'loading'}
														floId={floId}
														floIndex={floIndex}
													/>
												))}
											</div>
										)}
										{stepSpotlights?.length > 0 && (
											<div className={styles.spotlightsWrapper}>
												{/*@ts-ignore*/}
												<SpotlightSvg
													width={isVideo ? videoSize?.width : imageDims.width}
													height={isVideo ? videoSize?.height : imageDims.height}
													spotlights={stepSpotlights}
													id={get(step, 'id')}
												/>
											</div>
										)}
										{(imageReadyState === 'loaded' || videoReady) &&
											elementAvailable &&
											get(step, 'enabled') && (
												<div
													className={styles.uiActivityMouseClick}
													style={{
														top: pointerY - 24,
														left: pointerX - 24,
													}}
												>
													<Player
														autoplay
														loop
														speed={1}
														src={mouseAnimation}
														className={classNames(styles.infinity, styles.playerSize)}
													/>
												</div>
											)}
									</div>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Control bar - fixed at bottom */}
				{isSlideShow && (
					<div className={styles.slideToolbar}>
						<div
							className={styles.leftGp}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '16px',
							}}
						>
							<Tooltip
								classes={{
									popper: styles.tooltip,
								}}
								arrow
								title={'Prev'}
								placement={'top'}
							>
								<IconArrowLeft
									size={16}
									className={classNames(
										styles.slideToolbarIcon,
										index === 0 ? styles.disabled : ''
									)}
									onClick={() => {
										handlePrevious && index > 0 && handlePrevious();
									}}
									style={{ color: '#fff' }}
								/>
							</Tooltip>
							<span
								ref={spanRef}
								onClick={handleSpanClick}
								className={styles.slideToolbarSpan}
							>
								{index + 1}/{totalSlides}
							</span>
							{optionsVisible && (
								<div
									ref={optionsRef}
									className={classNames(styles.slideToolbarOptionsContainer)}
								>
									{Array.from({ length: totalSlides }, (_, i) => (
										<div
											key={i}
											onClick={() => handleOptionClick(i + 1)}
											className={classNames(styles.slideToolbarOptions)}
										>
											{i + 1}
										</div>
									))}
								</div>
							)}
							<Tooltip
								classes={{
									popper: styles.tooltip,
								}}
								arrow
								title={'Next'}
								placement={'top'}
							>
								<IconArrowRight
									size={16}
									className={classNames(
										styles.slideToolbarIcon,
										index === totalSlides - 1 ? styles.disabled : ''
									)}
									onClick={() => {
										handleNext && index < totalSlides - 1 && handleNext();
									}}
									style={{ color: '#fff' }}
								/>
							</Tooltip>
						</div>
						<div className={styles.rightGp}>
							{showPoweredBy && (
								<div className={classNames(styles.poweredbySlide)}>
									<a
										href={APP_WEBSITE_URL}
										target="_blank"
										className={styles.link}
										onClick={onPoweredByClick}
									>
										<span className={styles.powerBySlideTxt}>Powered by</span>
										<FloikLogo
											className={classNames(
												styles.floikSVGIconSlide,
												styles.invertedFilter
											)}
										/>
									</a>
								</div>
							)}
							<Tooltip
								classes={{
									popper: styles.tooltip,
								}}
								arrow
								title={'Reload'}
								placement={'top'}
							>
								<IconRotate2
									size={16}
									className={styles.slideToolbarIcon}
									onClick={() => {
										handleOptionClick(1);
									}}
								/>
							</Tooltip>
						</div>
					</div>
				)}
			</div>
		</li>
	);
};

const Instructions = ({
	hideHeader,
	hideHeaderBecauseOfData,
	floId,
	floIndex,
	themeColor,
	view,
	wrapper,
}: {
	hideHeader?: boolean;
	hideHeaderBecauseOfData?: boolean;
	floId: string;
	floIndex: number;
	themeColor: string;
	view?: string;
	wrapper?: string;
}) => {
	const dispatch = useDispatch();
	const steps = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.guide.steps`, [])
	);
	const floElements = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.guide.elements`, [])
	) as elementBeanListType[];
	const stepsFloElements = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.guide.stepsFloElements`, {})
	);
	const showAuthor = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails['show-author']`, true)
	);
	const showTOC = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails['show-toc']`, false)
	);
	const floName = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.heading`)
	);
	const internalArticles = useSelector((state: RootState) =>
		hideHeader
			? false
			: get(state, `floPage.flos.${floIndex}.floDetails.internalArticles`, false)
	) as boolean;
	const isEmbed: boolean = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["is-embed"]`, false)
	);
	const [style, setStyles] = useState({});
	const createdBy = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.createdBy.name`)
	);
	const [stepsRenderStatus, setStepsRenderStatus] = useState<Boolean[]>([]);
	const [isInitialRenderDone, setIsInitialRenderDone] = useState(false);

	const outputHeader: string = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.heading`, '')
	);

	const outputDesc: string = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.description`, '')
	);
	const showPoweredBy = useSelector(enablePoweredBYSelector);

	const onPoweredByClick = useCallback(() => {
		dispatch(
			trackClickAction({
				body: {
					buttonType: 'poweredByFloik',
				},
				floId,
				floIndex,
			})
		);
	}, [floId]);

	const isMicroFlo = isMicroApp();

	useEffect(() => {
		if (isMicroFlo && !isInitialRenderDone && steps.length && stepsRenderStatus.length) {
			const renderedItems = stepsRenderStatus.reduce(
				(count, item) => (item ? count + 1 : count),
				0
			);
			if (renderedItems === steps.length) {
				window.doc360PreviewInstance?.onMessageFromMicroApp({
					type: 'floik_app_flo_steps_rendered',
					payload: {
						initialRenderComplete: true,
					},
				});
				setIsInitialRenderDone(true);
			}
		}
	}, [stepsRenderStatus, steps, setIsInitialRenderDone, isInitialRenderDone, isMicroFlo]);

	const setRenderStatus = useCallback(
		(index: number) => {
			setStepsRenderStatus((cur) => {
				let newState = [...cur];
				newState[index] = true;
				return newState;
			});
		},
		[setStepsRenderStatus]
	);

	useEffect(() => {
		if (!internalArticles) return;
		const onResize = () => {
			const header = document.querySelector('.website-header-content');
			const instructions = document.querySelector(`.${parentStyles.leftGridContainer}`);
			const instructionsContent = document.querySelector(`.${styles.instructionContent}`);
			if (!header || !instructions || !instructionsContent) return;
			const { x } = header.getBoundingClientRect();
			let paddingLeft = max([x, 30]) as number;
			setStyles({
				paddingLeft,
			});
		};
		const resizeObserver = new ResizeObserver(onResize);
		resizeObserver.observe(document.body);
		onResize();
		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, [internalArticles, setStyles]);

	const isSlideShow = view === 'presentation';

	// If it's a slideshow, render the SlideShow component
	if (isSlideShow) {
		return (
			<SlideShow
				steps={steps}
				floId={floId}
				floIndex={floIndex}
				themeColor={themeColor}
				floElements={floElements}
				stepsFloElements={stepsFloElements}
				wrapper={wrapper}
				setRenderStatus={setRenderStatus}
				hideHeader={hideHeader}
				hideHeaderBecauseOfData={hideHeaderBecauseOfData}
				style={style}
				internalArticles={internalArticles}
				showPoweredBy={showPoweredBy}
				onPoweredByClick={onPoweredByClick}
				view={view}
				StepDetailsComponent={StepDetails}
			/>
		);
	}

	// For regular view (not slideshow), render the standard view
	return (
		<>
			<Grid
				item
				desktop={showTOC ? 9 : 12}
				laptop={showTOC ? 9 : 12}
				tablet={showTOC ? 8 : 12}
				mobile={12}
				style={style}
				classes={{
					root: classNames(
						parentStyles.leftGridContainer,
						styles.instructions,
						styles.container,
						{
							[parentStyles.gridWithoutHeader]: hideHeader,
							[styles.fullHeight]: isEmbed,
							[parentStyles.leftGridContainerWithWebsiteHeader]: internalArticles,
							[styles.withHeader]: !hideHeader && !hideHeaderBecauseOfData,
							[styles.withWebsiteHeader]: internalArticles,
						}
					),
				}}
			>
				<div
					className={classNames(
						styles['instruction-content'],
						styles.instructionContent,
						{
							[styles.fullHeight]: isMicroApp() || hideHeader,
						}
					)}
				>
					{!isMicroFlo && (
						<>
							<div className={styles.floNameWrapper}>
								<h1
									className={classNames('font-25 rubik-medium', styles.floName)}
									style={{
										color: getContrastColor(themeColor),
									}}
									dangerouslySetInnerHTML={{
										__html: sanitizeHtml(outputHeader || floName || ''),
									}}
								></h1>
								<ShareFloButton
									floIndex={floIndex}
									floId={floId}
									classes={{
										shareButton: styles.shareButton,
										shareIcon: styles.shareIcon,
									}}
									isDocument
								/>
							</div>
							<div
								className={classNames('font-14', styles.floDescription, {
									'margin-b-16': hideHeader,
									'margin-b-20': !hideHeader,
								})}
								style={{
									color: getContrastColor(themeColor),
									opacity: 0.7,
								}}
							>
								{showAuthor && (
									<>
										Created by <span className="rubik-medium">{createdBy}</span>
									</>
								)}
							</div>
						</>
					)}
					{outputDesc && (
						<div
							className={classNames('font-14', styles.description)}
							style={{
								color: getContrastColor(themeColor),
								opacity: 0.7,
							}}
							dangerouslySetInnerHTML={{
								__html: sanitizeHtmlLinks(outputDesc),
							}}
						></div>
					)}

					<ul
						className={classNames(
							styles['instruction-list'],
							'grey-color',
							'font-15',
							styles.instructionList
						)}
					>
						{steps.map((step: GuideDataType, index: number) => {
							const spotlights = get(
								stepsFloElements,
								`${get(step, 'stepId', '')}.spotlight`,
								[]
							);
							const texts = get(stepsFloElements, `${get(step, 'stepId', '')}.text`, []);
							const blursList = get(
								stepsFloElements,
								`${get(step, 'stepId', '')}.blur`,
								[]
							);
							const imageList = get(stepsFloElements, `${step.stepId}.image`, []);
							return (
								<StepDetails
									index={index}
									element={get(floElements, step.elementId)}
									step={step}
									key={`${get(step, 'id', '')}_${spotlights.length}_${blursList.length}_${
										imageList.length
									}`}
									blursList={blursList}
									imagesList={imageList}
									spotlights={spotlights}
									texts={texts}
									setRenderStatus={setRenderStatus}
									floId={floId}
									floIndex={floIndex}
									themeColor={themeColor}
									wrapper={wrapper}
									isSlideShow={false}
									totalSlides={steps.length}
									onRefReady={() => {}}
									floElements={floElements}
									steps={steps}
								/>
							);
						})}
					</ul>

					{showPoweredBy && (
						<div
							className={classNames(styles.poweredby, styles.poweredBy, {
								[styles.showPoweredBy]: !showTOC,
							})}
						>
							<a
								href={APP_WEBSITE_URL}
								target="_blank"
								className={styles.link}
								onClick={onPoweredByClick}
							>
								Powered by
								<FloikLogo className={styles.floikSVGIcon} />
							</a>
						</div>
					)}
				</div>
			</Grid>
			{showTOC && (
				<Grid
					item
					desktop={3}
					laptop={3}
					tablet={4}
					mobile={12}
					classes={{
						root: classNames(parentStyles.rightGridContainer, styles.stepsContainer, {
							[styles.withWebsiteHeader]: internalArticles,
							[parentStyles.gridWithoutHeader]: hideHeader,
						}),
					}}
				>
					<Steps
						steps={steps}
						internalArticles={internalArticles}
						hideHeader={hideHeader}
						themeColor={themeColor}
						onPoweredByClick={onPoweredByClick}
					/>
				</Grid>
			)}
		</>
	);
};

export default Instructions;
